/**
 * Wizard Step 2 - Counting (with Packaging support)
 *
 * Input counted quantities per product, with multiple packaging rows + bulk.
 * All packaging options from the catalog are displayed as stacked fields.
 * Auto-save via PUT /inventories/:id/products/:rawProductId/lines
 * @see US-21b.05 - Refonte WizardStep2
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Search, Package, ChevronRight, Printer, FileText, FileSpreadsheet } from 'lucide-react';
import { Input, Button, Dropdown, DropdownItem } from '@/components/ui';
import { formatQuantity, getQuantityStep } from '@/lib/format';
import { useSaveProductLines } from '../../hooks/useInventories';
import { inventoriesService } from '../../services/inventories.service';
import type {
  InventoryWithLines,
  InventoryProductGroup,
  InventoryLineInput,
} from '@bstock/shared';

interface WizardStep2Props {
  inventory: InventoryWithLines;
  onRefresh: () => void;
}

// Debounce delay for auto-save
const DEBOUNCE_MS = 800;

/**
 * State for a single product's form fields.
 * `rows` is an array of { packagingId, packagingQty, conversionFactor }
 * where packagingId=null means the bulk row.
 */
interface ProductFormState {
  rows: Array<{
    key: string; // local unique key for React
    packagingId: string | null;
    packagingQty: string; // string for input
    conversionFactor: number | null;
    label: string; // display label
  }>;
  notes: string;
}

/**
 * Build initial form state from a product group.
 * All packaging options from the catalog are displayed, plus the bulk row.
 * If a packaging option already has a saved line, use the saved value.
 */
function buildInitialState(group: InventoryProductGroup): ProductFormState {
  const rows: ProductFormState['rows'] = [];

  // Build a map of existing saved lines by (packagingId, conversionFactor)
  const savedLines = new Map<string, { packagingQty: number | null }>();
  for (const line of group.lines) {
    if (line.packagingId !== null) {
      const key = `${line.packagingId}::${line.conversionFactor}`;
      savedLines.set(key, { packagingQty: line.packagingQty });
    }
  }

  // Add all packaging options from the catalog
  for (const option of group.packagingOptions) {
    const key = `${option.packagingId}::${option.conversionFactor}`;
    const saved = savedLines.get(key);
    rows.push({
      key: `pkg-${option.packagingId}-${option.conversionFactor}`,
      packagingId: option.packagingId,
      packagingQty: saved?.packagingQty != null ? saved.packagingQty.toString() : '',
      conversionFactor: option.conversionFactor,
      label: option.label,
    });
  }

  // Bulk line last
  const bulkLine = group.lines.find((l) => l.packagingId === null);
  rows.push({
    key: 'bulk',
    packagingId: null,
    packagingQty: bulkLine && bulkLine.countedQty !== null ? bulkLine.countedQty.toString() : '',
    conversionFactor: null,
    label: `Vrac (${group.recipeUnitSymbol})`,
  });

  const notes = bulkLine?.notes ?? '';

  return { rows, notes };
}

function computeTotalFromState(state: ProductFormState): number {
  let total = 0;
  for (const row of state.rows) {
    const qty = parseFloat(row.packagingQty);
    if (isNaN(qty) || qty <= 0) continue;
    if (row.packagingId === null) {
      total += qty;
    } else {
      total += qty * (row.conversionFactor ?? 1);
    }
  }
  return total;
}

export function WizardStep2({ inventory, onRefresh }: WizardStep2Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);
  const [formStates, setFormStates] = useState<Record<string, ProductFormState>>({});
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});
  const saveProductLinesMutation = useSaveProductLines();

  // Use product groups from inventory
  const productGroups = inventory.productGroups ?? [];

  // Group products by category
  const groupsByCategory = useMemo(() => {
    let filtered = productGroups;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = productGroups.filter(
        (g) =>
          g.rawProductName.toLowerCase().includes(q) ||
          g.rawProductSku.toLowerCase().includes(q)
      );
    }

    const grouped = new Map<
      string,
      { category: { id: string; name: string; code: string }; groups: InventoryProductGroup[] }
    >();

    for (const group of filtered) {
      const catId = group.categoryId;
      if (!grouped.has(catId)) {
        grouped.set(catId, {
          category: { id: catId, name: group.categoryName, code: group.categoryCode },
          groups: [],
        });
      }
      grouped.get(catId)!.groups.push(group);
    }

    // Sort categories by code, products within each category by name
    const result = Array.from(grouped.values());
    result.sort((a, b) => a.category.code.localeCompare(b.category.code));
    for (const cat of result) {
      cat.groups.sort((a, b) => a.rawProductName.localeCompare(b.rawProductName));
    }
    return result;
  }, [productGroups, searchQuery]);

  // Initialize form states from product groups
  useEffect(() => {
    const initial: Record<string, ProductFormState> = {};
    for (const group of productGroups) {
      initial[group.rawProductId] = buildInitialState(group);
    }
    setFormStates(initial);
  }, [inventory.id]); // Only on inventory change

  // Save product lines to backend
  const saveProduct = useCallback(
    async (rawProductId: string, state: ProductFormState) => {
      const lines: InventoryLineInput[] = [];

      for (const row of state.rows) {
        const qty = parseFloat(row.packagingQty);
        if (isNaN(qty) || qty < 0) continue;
        lines.push({
          packagingId: row.packagingId,
          packagingQty: qty,
          conversionFactor: row.conversionFactor,
        });
      }

      try {
        await saveProductLinesMutation.mutateAsync({
          inventoryId: inventory.id,
          rawProductId,
          data: {
            notes: state.notes || undefined,
            lines,
          },
        });
        onRefresh();
      } catch {
        // Error handled by mutation
      }
    },
    [inventory.id, saveProductLinesMutation, onRefresh]
  );

  // Handle field change with debounce
  const handleFieldChange = useCallback(
    (rawProductId: string, rowIndex: number, value: string) => {
      setFormStates((prev) => {
        const state = { ...prev[rawProductId] };
        if (!state.rows) return prev;
        const newRows = [...state.rows];
        newRows[rowIndex] = { ...newRows[rowIndex], packagingQty: value };
        const newState = { ...state, rows: newRows };

        // Debounce save
        if (debounceTimers.current[rawProductId]) {
          clearTimeout(debounceTimers.current[rawProductId]);
        }
        debounceTimers.current[rawProductId] = setTimeout(() => {
          saveProduct(rawProductId, newState);
        }, DEBOUNCE_MS);

        return { ...prev, [rawProductId]: newState };
      });
    },
    [saveProduct]
  );

  // Handle blur: save immediately
  const handleBlur = useCallback(
    (rawProductId: string) => {
      if (debounceTimers.current[rawProductId]) {
        clearTimeout(debounceTimers.current[rawProductId]);
        delete debounceTimers.current[rawProductId];
      }
      const state = formStates[rawProductId];
      if (state) {
        saveProduct(rawProductId, state);
      }
    },
    [formStates, saveProduct]
  );

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(clearTimeout);
    };
  }, []);

  // Get variance display for a product
  const getVarianceDisplay = (group: InventoryProductGroup, state?: ProductFormState) => {
    if (!state) return null;
    const totalCounted = computeTotalFromState(state);
    if (totalCounted === 0 && state.rows.every((r) => r.packagingQty === '')) return null;

    const theoretical = group.theoreticalQty ?? 0;
    const variance = totalCounted - theoretical;
    if (variance === 0) return null;

    return (
      <span className={variance < 0 ? 'text-danger-600' : 'text-success-600'}>
        {variance > 0 ? '+' : ''}{formatQuantity(variance)} {group.recipeUnitSymbol}
      </span>
    );
  };

  const handleDownloadSheet = async (format: 'pdf' | 'excel') => {
    try {
      await inventoriesService.downloadSheet(inventory.id, format);
    } catch {
      // Error silently handled
    }
  };

  return (
    <div className="space-y-4">
      {/* Search + Export button */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 z-10" />
          <Input
            type="text"
            placeholder="Rechercher un produit..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Dropdown
          trigger={
            <Button variant="secondary" size="sm">
              <Printer className="w-4 h-4 mr-2" />
              Feuille de saisie
            </Button>
          }
        >
          <DropdownItem onClick={() => handleDownloadSheet('pdf')}>
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </DropdownItem>
          <DropdownItem onClick={() => handleDownloadSheet('excel')}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </DropdownItem>
        </Dropdown>
      </div>

      {/* Instructions */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 text-sm text-primary-700">
        Saisissez les quantites comptees par packaging ou en vrac. Les modifications sont sauvegardees automatiquement.
      </div>

      {/* Products by category */}
      <div className="space-y-4">
        {groupsByCategory.length === 0 ? (
          <div className="text-center py-8 text-neutral-500">
            Aucun produit trouve
          </div>
        ) : (
          groupsByCategory.map(({ category, groups }) => (
            <div
              key={category.id}
              className="border border-neutral-200 rounded-lg overflow-hidden"
            >
              <button
                type="button"
                onClick={() =>
                  setExpandedCategoryId((prev) =>
                    prev === category.id ? null : category.id
                  )
                }
                aria-expanded={expandedCategoryId === category.id}
                className="w-full bg-neutral-100 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-neutral-200 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <ChevronRight
                    className={`w-4 h-4 text-neutral-500 transition-transform duration-150 ${
                      expandedCategoryId === category.id ? 'rotate-90' : ''
                    }`}
                  />
                  <span className="font-medium text-neutral-700">{category.name}</span>
                </div>
                <span className="text-sm text-neutral-500">
                  {groups.filter((g) => {
                    const s = formStates[g.rawProductId];
                    return s && s.rows.some((r) => r.packagingQty !== '' && r.packagingQty !== undefined);
                  }).length} / {groups.length}
                </span>
              </button>
              {expandedCategoryId === category.id && <div className="divide-y divide-neutral-100">
                {groups.map((group) => {
                  const state = formStates[group.rawProductId];
                  const totalCounted = state ? computeTotalFromState(state) : 0;

                  return (
                    <div key={group.rawProductId} className="px-4 py-3">
                      {/* Product header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Package className="w-4 h-4 text-neutral-400 flex-shrink-0" />
                            <span className="font-medium text-dark-500 truncate">
                              {group.rawProductName}
                            </span>
                          </div>
                          <div className="text-xs text-neutral-500 ml-6">
                            {group.rawProductSku}
                          </div>
                        </div>

                        {/* Theoretical qty */}
                        <div className="text-right mr-4">
                          <div className="text-xs text-neutral-500">Theorique</div>
                          <div className="text-sm text-neutral-700">
                            {group.theoreticalQty ?? 0} {group.recipeUnitSymbol}
                          </div>
                        </div>

                        {/* Total counted */}
                        <div className="text-right mr-4">
                          <div className="text-xs text-neutral-500">Total compte</div>
                          <div className="text-sm font-medium text-dark-500">
                            {formatQuantity(totalCounted)} {group.recipeUnitSymbol}
                          </div>
                        </div>

                        {/* Variance */}
                        <div className="w-24 text-right text-sm">
                          {getVarianceDisplay(group, state)}
                        </div>
                      </div>

                      {/* Rows: all packaging options + bulk */}
                      {state && (
                        <div className="ml-6 space-y-1">
                          {state.rows.map((row, rowIndex) => {
                            const isBulk = row.packagingId === null;
                            const rowCountedQty = (() => {
                              const qty = parseFloat(row.packagingQty);
                              if (isNaN(qty) || qty <= 0) return 0;
                              if (isBulk) return qty;
                              return qty * (row.conversionFactor ?? 1);
                            })();

                            return (
                              <div key={row.key} className="flex items-center gap-2">
                                {/* Row label */}
                                <div className="w-48 text-xs text-neutral-500 truncate">
                                  {isBulk ? (
                                    <span className="italic">{row.label}</span>
                                  ) : (
                                    row.label
                                  )}
                                </div>

                                {/* Qty input */}
                                <div className="w-28">
                                  <Input
                                    type="number"
                                    value={row.packagingQty}
                                    onChange={(e) =>
                                      handleFieldChange(group.rawProductId, rowIndex, e.target.value)
                                    }
                                    onBlur={() => handleBlur(group.rawProductId)}
                                    placeholder="0"
                                    min={0}
                                    max={10000}
                                    step={getQuantityStep()}
                                    className="h-8 text-right text-sm"
                                  />
                                </div>

                                {/* Conversion result */}
                                {!isBulk && (
                                  <div className="text-xs text-neutral-400 w-32 text-right">
                                    = {formatQuantity(rowCountedQty)} {group.recipeUnitSymbol}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
